// src/services/wipeLocal.js — everything this browser learned, removed.
//
// 🟥 THREE EXITS WIPED THREE DIFFERENT SUBSETS, AND NONE OF THEM WIPED THE
//    HISTORY. Signing out cleared the media store (App.jsx). The phone's remote
//    "Log out" cleared the identity key AND the media store (session.js). Both
//    authors were thinking about exactly this problem — the comment above the
//    manual logout says in as many words that decrypted media must not outlive
//    the session — and both missed `allo_web_history`, because historySync.js
//    added it afterwards and nobody was holding the list.
//
//    The effect: log out on a shared laptop and the whole exported chat history
//    sat on that disk in plaintext, readable by the next person with no session
//    and no key. The forgetting was the feature; it had no owner.
//
// 🟢 SO THE LIST LIVES IN ONE PLACE AND EVERY EXIT CALLS IT. A store added later
//    is added here, once, and all three paths inherit it — which is the only
//    arrangement that survives the next cache somebody adds.
//
// 🟥 AND IT IS ORDERED BY WHAT IT COSTS TO LEAVE BEHIND. Message plaintext
//    first, then media, then the key, then preferences: if the tab is killed
//    halfway through a wipe, what remains should be the least revealing thing,
//    not whatever happened to be last in the object.

/** Every IndexedDB database this client writes, most sensitive first. */
export const LOCAL_DATABASES = [
  'allo_web_history',   // 🟥 decrypted message text, keyed by uid
  'allo_web_media',     // decrypted photos, voice notes, documents
  'allo-guest-outbox',  // unsent guest messages, in the clear
  'allo_web_identity',  // this browser's private key
  'allo_web_vault',     // the wrapped data key — useless without it, but it is ours
];

/* 🟥 A WIPE-LIST IS FORGOTTEN BY DEFAULT; A KEEP-LIST IS REMEMBERED BY DEFAULT.
   The first version of this file listed the keys to remove — and I wrote three
   of the four from memory and got them wrong (`allo:np:` for `allo_np_`,
   `allo_calls_` for `allo_local_call_rows`), which is the same failure that put
   the history on disk in the first place: a list of everything sensitive, kept
   by hand, with nothing to notice an omission.

   So the rule is inverted. Everything this app stores goes, EXCEPT a short list
   of things that describe the MACHINE rather than the account. A cache someone
   adds next month is wiped without anyone remembering to add it; the mistake it
   invites is a window width being reset, not a conversation being left behind. */
const OWNED_PREFIXES = [/^allo[_:]/, /^feed\./];

/** Machine preferences — no account content, and losing them only annoys. */
export const KEEP_KEYS = new Set([
  'allo:vb-right-w',        // panel widths on this screen
  'allo:vb-reels-w',
  'allo_call_thread_w',
  'allo_gcd_layout',        // group-call tile layout
  'allo_call_dock_mode',    // docked / floating / tucked — a choice about this screen
  'allo_call_dock_pos',
  'allo:volume',
  'allo_cookie_consent_v1', // consent given by the person at this machine
]);

/** Is this key ours to remove when an account leaves this browser? */
export function isAccountKey(key) {
  if (!key || KEEP_KEYS.has(key)) return false;
  return OWNED_PREFIXES.some((re) => re.test(key));
}

function dropDatabase(name) {
  return new Promise((resolve) => {
    let req;
    try { req = indexedDB.deleteDatabase(name); } catch { resolve(false); return; }
    /* 🟥 `blocked` FIRES AND NOTHING ELSE DOES when another tab still holds the
       database open — a second Allo tab is the ordinary case, not the exotic
       one. Without this the promise never settles and the wipe stops dead
       partway through, leaving exactly the stores it had not reached yet. */
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
    req.onblocked = () => resolve(false);
    setTimeout(() => resolve(false), 3000);
  });
}

function dropKeys() {
  let removed = 0;
  try {
    /* 🟥 COLLECT, THEN DELETE. localStorage.key(i) re-indexes on every removal,
       so deleting inside the walk skips every other match — the classic
       mutate-while-iterating bug, and here it silently leaves data behind. */
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (isAccountKey(k)) doomed.push(k);
    }
    for (const k of doomed) { try { localStorage.removeItem(k); removed += 1; } catch { /* full */ } }
  } catch { /* private mode */ }
  return removed;
}

/* 🟢 THE CONTENT DATABASES ALONE — everything an archive, nothing an identity.
   Dropped on idle while the session stays signed in, so the machine holds
   nothing and the person is not logged out for having gone to lunch. */
export const PLAINTEXT_DATABASES = ['allo_web_history', 'allo_web_media'];

/**
 * Forget what this browser has read, without forgetting who it is.
 *
 * 🟥 THE KEY AND THE SESSION SURVIVE ON PURPOSE, and that is the whole point of
 *    having a second function. wipeLocalData() is an exit; this is a nap. Taking
 *    the identity key here would turn "you were away for a day" into "re-approve
 *    this browser from your phone", which is the annoyance this design exists to
 *    avoid — and people who are annoyed by security turn it off.
 */
export async function wipePlaintext() {
  const dropped = [];
  const failed = [];
  for (const name of PLAINTEXT_DATABASES) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await dropDatabase(name);
    (ok ? dropped : failed).push(name);
  }
  if (failed.length) console.warn('[wipe] plaintext left behind:', failed.join(', '));
  return { dropped, failed };
}

/**
 * Remove every trace of this account from this browser.
 *
 * 🟢 NEVER THROWS, AND ALWAYS REACHES THE END. A wipe that gives up on its first
 *    failure leaves more behind than one that does not run at all, because the
 *    caller believes it worked. Each store is attempted independently and the
 *    result says what actually went.
 */
export async function wipeLocalData() {
  const dropped = [];
  const failed = [];
  for (const name of LOCAL_DATABASES) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await dropDatabase(name);
    (ok ? dropped : failed).push(name);
  }
  const keys = dropKeys();
  if (failed.length) console.warn('[wipe] could not remove:', failed.join(', '));
  return { dropped, failed, keys };
}
